import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API, { formatApiError } from "@/lib/api";
import RevealRenderer from "@/components/RevealRenderer";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Eye, HelpCircle } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";

// Keep in sync with backend VALID_* sets.
// 2026-05-03 (v6.0) — `technology_operations` removed; the live intake only
// surfaces the four primary lenses (software / services / cloud / advisory),
// and lens_rewrite.py framing rules don't exist for tech_ops.

// Renders an ISO-ish date as "Tue 22 Apr" — short enough for the cycle label.
function formatCycleDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
    return d.toLocaleDateString("en-GB", {
      weekday: "short", day: "2-digit", month: "short",
    });
  } catch {
    return String(iso).slice(0, 10);
  }
}

const COMPANY_TYPES = [
  { id: "software", label: "SaaS & software" },
  { id: "services", label: "Services & delivery" },
  { id: "cloud", label: "Cloud & infrastructure" },
  { id: "advisory", label: "Consulting & advisory" },
];
// 2026-05-03 (v6.0) — `leadership` removed; seniority is captured separately
// on the seniority dropdown (individual / manager / pl_owner). Keeping it as
// a role-family option was a legacy double-categorisation.
const ROLE_FAMILIES = [
  { id: "acquisition", label: "Acquisition" },
  { id: "retention", label: "Retention" },
  { id: "solutioning", label: "Solutioning" },
  { id: "delivery", label: "Delivery" },
];
const SENIORITY_TIERS = [
  { id: "individual", label: "Individual contributor" },
  { id: "leader", label: "Account / practice leader" },
  { id: "pl_owner", label: "P&L owner" },
];
// Wave 3f — capability bets multi-select (up to 3), keyed on stable backend enums.
const CAPABILITY_BETS = [
  { id: "enterprise_saas", label: "Enterprise software & SaaS" },
  { id: "ai_data", label: "AI & data platforms" },
  { id: "integration_architecture", label: "Integration & architecture" },
  { id: "cloud_infra", label: "Cloud & infrastructure" },
  { id: "risk_compliance", label: "Risk, compliance & regtech" },
  { id: "managed_services", label: "Managed services & outsourcing" },
];

// Chunk 3 / TASK 2 — experience-level calibration knob. Replaces "capability
// bets" in the live intake schema (which is now boost-only). The synthesis
// path adapts how much account context each action carries based on this.
const EXPERIENCE_LEVELS = [
  { id: "new_to_account", label: "New to this account" },
  { id: "familiar", label: "Familiar with the account" },
  { id: "deeply_known", label: "Deeply knows the account" },
];

// Kept in sync with AdminAccountsPage / IntelligenceView — seeded at mount and
// refreshed from /accounts when the page loads.
const DEFAULT_ACCOUNTS = [
  { id: "deutsche_bank", name: "Deutsche Bank" },
  { id: "lloyds_banking_group", name: "Lloyds Banking Group" },
];

export default function AdminPreviewPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [accounts, setAccounts] = useState(DEFAULT_ACCOUNTS);
  const [reveal, setReveal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Form state — seeded from URL query (so admin can bookmark / share a lens).
  const [account, setAccount] = useState(searchParams.get("account") || "deutsche_bank");
  const [companyType, setCompanyType] = useState(searchParams.get("company_type") || "software");
  const [roleFamily, setRoleFamily] = useState(searchParams.get("role_family") || "acquisition");
  const [seniority, setSeniority] = useState(searchParams.get("seniority_tier") || "individual");
  // Chunk 3 / TASK 2 — experience-level calibration. Drives how much account
  // context each action carries.
  const [experienceLevel, setExperienceLevel] = useState(
    searchParams.get("experience_level") || "familiar",
  );
  // Wave 3f — capability bets (up to 3); URL-persisted as comma-separated list.
  const [capabilityBets, setCapabilityBets] = useState(() => {
    const raw = searchParams.get("capability_bets") || "";
    return raw.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 3);
  });

  // Auth guard
  useEffect(() => {
    if (user === null) { navigate("/signin"); return; }
    if (user === undefined) return;
    if (!user.is_admin) {
      toast.error("Admin access required");
      navigate("/intelligence");
    }
  }, [user, navigate]);

  // Pull the live accounts catalog so the dropdown matches admin flips.
  useEffect(() => {
    API.get("/accounts")
      .then(({ data }) => {
        const live = (data.accounts || []).filter((a) => a.status === "live");
        if (live.length) setAccounts(live);
      })
      .catch(() => {
        /* fall back to DEFAULT_ACCOUNTS */
      });
  }, []);

  const fetchPreview = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await API.get("/admin/preview/reveal", {
        params: {
          account,
          company_type: companyType,
          role_family: roleFamily,
          seniority_tier: seniority,
          experience_level: experienceLevel,
          ...(capabilityBets.length ? { capability_bets: capabilityBets.join(",") } : {}),
        },
      });
      setReveal(data);
      // Reflect state in the URL so the lens is bookmarkable.
      setSearchParams(
        {
          account,
          company_type: companyType,
          role_family: roleFamily,
          seniority_tier: seniority,
          experience_level: experienceLevel,
          ...(capabilityBets.length ? { capability_bets: capabilityBets.join(",") } : {}),
        },
        { replace: true },
      );
    } catch (err) {
      setReveal(null);
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [account, companyType, roleFamily, seniority, experienceLevel, capabilityBets, setSearchParams]);

  // Fire initial fetch when auth resolves — avoids flashing an empty panel
  // when the admin arrives via a shared URL.
  useEffect(() => {
    if (user?.is_admin) {
      fetchPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.is_admin]);

  const fullReveal = useMemo(() => {
    if (!reveal?.full_reveal) return null;
    try {
      return typeof reveal.full_reveal === "string"
        ? JSON.parse(reveal.full_reveal)
        : reveal.full_reveal;
    } catch {
      return null;
    }
  }, [reveal]);

  if (user === undefined) {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center">
        <p className="text-sm text-zinc-900">Loading…</p>
      </div>
    );
  }
  if (!user?.is_admin) return null;

  const accountName = reveal?.account_name || accounts.find((a) => a.id === account)?.name || "";
  const companyLabel = COMPANY_TYPES.find((c) => c.id === companyType)?.label || companyType;
  const roleLabel = ROLE_FAMILIES.find((r) => r.id === roleFamily)?.label || roleFamily;
  const seniorityLabel = SENIORITY_TIERS.find((s) => s.id === seniority)?.label || seniority;
  const experienceLabel = EXPERIENCE_LEVELS.find((e) => e.id === experienceLevel)?.label || experienceLevel;

  return (
    <div className="min-h-screen bg-zinc-100" data-testid="admin-preview-page">
      {/* Admin preview ribbon (sticky) */}
      <div className="bg-zinc-950 text-zinc-100 sticky top-0 z-20" data-testid="admin-preview-ribbon">
        <div className="max-w-6xl mx-auto px-6 py-2 flex items-center justify-between text-[12px] font-mono">
          <div className="flex items-center gap-2">
            <Eye className="w-3.5 h-3.5 text-amber-500" strokeWidth={1.5} />
            <span className="text-amber-500">ADMIN · PREVIEW LENS</span>
            <span className="text-zinc-400">·</span>
            <span>{accountName || "—"}</span>
            <span className="text-zinc-400">·</span>
            <span>{companyLabel}</span>
            <span className="text-zinc-400">·</span>
            <span>{roleLabel}</span>
            <span className="text-zinc-400">·</span>
            <span>{seniorityLabel}</span>
            <span className="text-zinc-400">·</span>
            <span>{experienceLabel}</span>
            {capabilityBets.length > 0 && (
              <>
                <span className="text-zinc-400">·</span>
                <span className="text-amber-400">
                  {capabilityBets.map((c) => CAPABILITY_BETS.find((x) => x.id === c)?.label || c).join(" + ")}
                </span>
              </>
            )}
          </div>
          <div className="flex items-center gap-4">
            <Link to="/admin/eval" className="text-zinc-300 hover:text-white transition-colors" data-testid="admin-preview-eval-link">
              Eval harness →
            </Link>
            <Link to="/admin/accounts" className="text-zinc-300 hover:text-white transition-colors inline-flex items-center gap-1" data-testid="admin-preview-back-link">
              <ArrowLeft className="w-3 h-3" strokeWidth={1.5} />
              Back to admin
            </Link>
          </div>
        </div>
      </div>

      {/* Lens picker. Order is deliberate: 1) all selectors, 2) capability
          bets boost row, 3) Preview action button at the very bottom. The
          admin builds a complete lens before triggering the calibration. */}
      <div className="bg-white border-b border-zinc-200">
        <div className="max-w-6xl mx-auto px-6 py-5">
          {/* Step 1 — primary lens dimensions. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
            <LensSelect
              label="Account"
              value={account}
              onChange={setAccount}
              options={accounts.map((a) => ({ id: a.id, label: a.name }))}
              testId="preview-account-select"
            />
            <LensSelect
              label="Company type"
              value={companyType}
              onChange={setCompanyType}
              options={COMPANY_TYPES}
              testId="preview-company-type-select"
            />
            <LensSelect
              label="Role family"
              value={roleFamily}
              onChange={setRoleFamily}
              options={ROLE_FAMILIES}
              testId="preview-role-family-select"
            />
            <LensSelect
              label="Seniority"
              value={seniority}
              onChange={setSeniority}
              options={SENIORITY_TIERS}
              testId="preview-seniority-select"
            />
            <LensSelect
              label="Experience level"
              value={experienceLevel}
              onChange={setExperienceLevel}
              options={EXPERIENCE_LEVELS}
              testId="preview-experience-level-select"
            />
          </div>

          {/* Step 2 — capability bets (up to 3, optional). Acts as a boost,
              never a filter. Synthesis prompts no longer consume this; kept
              live for legacy QA replay. */}
          <div className="mt-4 pt-4 border-t border-zinc-100" data-testid="preview-capability-picker">
            <div className="flex items-baseline justify-between mb-2">
              <span className="text-[11px] uppercase tracking-widest text-zinc-700 font-medium">
                Capability bets (up to 3, optional)
              </span>
              <span className="text-[11px] text-zinc-600">
                {capabilityBets.length}/3 selected · boosts matching actions (not a filter)
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {CAPABILITY_BETS.map((c) => {
                const selected = capabilityBets.includes(c.id);
                const disabled = !selected && capabilityBets.length >= 3;
                return (
                  <button
                    key={c.id}
                    type="button"
                    disabled={disabled}
                    onClick={() => {
                      setCapabilityBets((prev) =>
                        prev.includes(c.id)
                          ? prev.filter((x) => x !== c.id)
                          : prev.length >= 3
                          ? prev
                          : [...prev, c.id],
                      );
                    }}
                    className={`text-[12px] px-3 py-1.5 border transition-colors ${
                      selected
                        ? "bg-amber-700 text-white border-amber-700"
                        : disabled
                        ? "bg-zinc-100 text-zinc-400 border-zinc-200 cursor-not-allowed"
                        : "bg-white text-zinc-900 border-zinc-300 hover:bg-zinc-50"
                    }`}
                    data-testid={`preview-capability-${c.id}`}
                  >
                    {c.label}
                  </button>
                );
              })}
              {capabilityBets.length > 0 && (
                <button
                  type="button"
                  onClick={() => setCapabilityBets([])}
                  className="text-[12px] px-3 py-1.5 border border-zinc-300 text-zinc-700 hover:bg-zinc-50"
                  data-testid="preview-capability-clear"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Step 3 — action row. Right-aligned so the eye lands on the
              button after sweeping the form left-to-right, top-to-bottom. */}
          <div
            className="mt-5 pt-4 border-t border-zinc-100 flex items-center justify-between gap-3 flex-wrap"
            data-testid="preview-action-row"
          >
            <span className="text-[11px] text-zinc-600">
              All selections set above will be applied when you preview.
            </span>
            <Button
              onClick={fetchPreview}
              disabled={loading}
              className="bg-amber-700 hover:bg-amber-800 text-white rounded-none text-sm font-medium h-10 px-6 disabled:opacity-50"
              data-testid="preview-apply-btn"
            >
              {loading ? "Calibrating…" : "Preview as this lens"}
            </Button>
          </div>
        </div>
      </div>

      {/* Reveal body */}
      <div className="max-w-6xl mx-auto px-6 py-10">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 mb-6" data-testid="preview-error">
            {error}
          </div>
        )}

        {loading && !fullReveal && (
          <div className="text-sm text-zinc-700" data-testid="preview-loading">
            Calibrating reveal for {companyLabel} · {roleLabel} · {seniorityLabel}…
          </div>
        )}

        {fullReveal && (
          <>
            {/* Wave 3e — stale-ribbon if any axis is out of date. */}
            {reveal?.reveal_revisions?.is_stale && (
              <div className="bg-red-50 border border-red-200 text-red-800 text-[12px] px-4 py-2 mb-4 flex items-center gap-2" data-testid="preview-stale-ribbon">
                <span className="uppercase tracking-widest font-medium">Stale</span>
                <span>Stored reveal is out of date on:</span>
                <span className="font-mono">{(reveal.reveal_revisions.mismatched_axes || []).join(" · ") || "—"}</span>
                <span className="text-red-700">· Refresh from /admin/accounts</span>
              </div>
            )}

            {/* Minimal header + signal sentence, mirrors IntelligenceView treatment */}
            <div className="mb-8">
              <h1 className="text-2xl sm:text-3xl font-medium tracking-tight text-zinc-950 font-heading">
                {accountName} — preview
              </h1>
              {/* 2026-05-03 — surface which weekly cycle this preview pertains
                  to. Operators were getting confused that the preview was a
                  detached "test page"; in fact it shows whatever the live
                  weekly cycle is for the account. Falls back to dash when
                  the harvest hasn't been run. */}
              {(reveal?.generation_cycle || reveal?.harvested_at || reveal?.reveal_date) && (
                <p
                  className="mt-1 text-[12px] text-zinc-700 uppercase tracking-widest"
                  data-testid="preview-cycle-label"
                >
                  {reveal?.generation_cycle && (
                    <>
                      Cycle <span className="font-mono text-zinc-950">{reveal.generation_cycle}</span>
                    </>
                  )}
                  {reveal?.harvested_at && (
                    <>
                      {reveal?.generation_cycle && <span className="text-zinc-400 mx-2">·</span>}
                      Harvested <span className="font-mono text-zinc-950">{formatCycleDate(reveal.harvested_at)}</span>
                    </>
                  )}
                  {reveal?.next_cycle && (
                    <>
                      <span className="text-zinc-400 mx-2">·</span>
                      Next refresh <span className="font-mono text-zinc-950">{formatCycleDate(reveal.next_cycle)}</span>
                    </>
                  )}
                </p>
              )}
              <p className="text-[15px] md:text-[17px] text-zinc-950 mt-4 leading-relaxed" data-testid="preview-signal-sentence">
                {reveal?.signal_sentence || fullReveal?.signal_sentence || ""}
              </p>
            </div>

            {/* Chunk 3 — lens audit summary. Surfaces the per-reveal audit
                fields the operator already sees on /admin/accounts: how many
                actions had their lens variant overlaid for the picked
                company_type, plus the named-person / citation / tier-diff
                guardrail counters. Read-only; mirrors generation_manifest. */}
            <LensAuditSummary
              fullReveal={fullReveal}
              companyType={companyType}
            />

            {/* Chunk 3.11 (2026-05-05) — Action-set QA debug panel.
                Surfaces the deterministic QA gate output (mutual
                exclusivity, tone violations, citation warnings) so the
                operator can diagnose action-card quality without
                inspecting the Mongo payload. */}
            <ActionSetQaPanel qa={fullReveal?.action_set_qa} />

            {/* Instruction 3 — Automated Checks. On-demand 6-check inspection
                of the previewed reveal. Calls /api/eval/automated-checks. */}
            <AutomatedChecksPanel
              accountName={accountName}
              generationCycle={reveal?.generation_cycle || ""}
            />

            {/* Instruction 5 Phase B — Citation Check. Audits every cited
                source for live URL / wayback / unavailable status, and
                which cards cite it. Calls /api/eval/citation-check/:id. */}
            <CitationCheckPanel revealId={reveal?.reveal_id || reveal?.id || ""} />

            <RevealRenderer
              reveal={fullReveal}
              accountName={accountName}
              weekLabel=""
              mode="coffee"
              accountId={account}
              revealCycle={reveal?.generation_cycle || ""}
              isLite={false}
            />
          </>
        )}
      </div>
    </div>
  );
}

function LensSelect({ label, value, onChange, options, testId }) {
  return (
    <label className="flex flex-col gap-1 text-[11px] uppercase tracking-widest text-zinc-700 font-medium">
      {label}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="border border-zinc-300 bg-white text-[13px] text-zinc-950 rounded-none h-10 px-2 font-sans focus:outline-none focus:border-amber-700 font-normal tracking-normal"
        data-testid={testId}
      >
        {options.map((o) => (
          <option key={o.id} value={o.id}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

// Chunk 3 — lens audit summary row.
// Reads `lens_rewrite_audit`, `named_person_audit`, `citation_audit`, and
// `tier_differentiation_score` off the calibrated reveal and renders the same
// 4 pill family that lives on /admin/accounts. Hidden when the reveal is
// pre-Chunk-3 and the audit fields are absent. Also computes a per-card "lens
// variant applied" counter from the in-flight `lens_variant_applied` stamps
// that calibrate_reveal sets when it overlays a per-lens variant.
function LensAuditSummary({ fullReveal, companyType }) {
  if (!fullReveal) return null;

  const actions = Array.isArray(fullReveal.actions) ? fullReveal.actions : [];
  const variantApplied = actions.filter(
    (a) => a && a.lens_variant_applied === companyType,
  ).length;

  const lr = fullReveal.lens_rewrite_audit || null;
  const np = fullReveal.named_person_audit || null;
  const ca = fullReveal.citation_audit || null;
  const td = typeof fullReveal.tier_differentiation_score === "number"
    ? fullReveal.tier_differentiation_score
    : null;

  // Hide entire row only when nothing surfaces. Prevents a confusing empty
  // strip on legacy reveals.
  if (!lr && !np && !ca && td === null) return null;

  return (
    <div
      className="mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2"
      data-testid="preview-lens-audit-summary"
    >
      {/* Pill 1 — lens variants */}
      <AuditPill
        label="Lens variant"
        testId="preview-pill-lens-variant"
        title={
          variantApplied > 0
            ? `${variantApplied} of ${actions.length} cards rendered with a pre-computed ${companyType} variant. Original titles + bodies preserved on _lens_original_*.`
            : `No card needed a variant — every card was already native ${companyType}.`
        }
        info={
          <>
            <p>
              Kalib AI pre-computes lens-specific rewrites for every action at
              synthesis time, so a Software user, a Services user, a Cloud
              user, and an Advisory user all see language native to their
              vendor type — without any read-time LLM latency.
            </p>
            <p>
              <strong>Rendered N/M</strong> — of the M cards shown to this
              user, N had their title + body swapped to the pre-built variant
              matching the picked lens. Originals are preserved as
              <code className="font-mono text-[11px]"> _lens_original_*</code>.
            </p>
            <p>
              <strong>In catalogue T</strong> — at synthesis, the system
              built T total variants across all (tier × lens) combinations.
              The user only ever sees the few that match their lens; the
              rest are stored alternates so a lens switch is instant.
            </p>
          </>
        }
      >
        <span
          className={`px-1.5 py-0.5 border uppercase tracking-widest font-mono ${
            variantApplied > 0
              ? "bg-amber-50 text-amber-800 border-amber-300"
              : "bg-emerald-50 text-emerald-800 border-emerald-300"
          }`}
        >
          rendered {variantApplied}/{actions.length}
        </span>
        {lr && (
          <span
            className="px-1.5 py-0.5 border bg-white text-zinc-700 border-zinc-300 uppercase tracking-widest font-mono"
            title={
              `Total per-lens variants pre-built at synthesis across all (tier × lens) combinations. The user only sees the ${actions.length} matching their lens; the rest are stored as alternates so a lens switch is instant.`
            }
          >
            in catalogue {lr.total_rewritten || 0}
          </span>
        )}
      </AuditPill>

      {/* Pill 2 — named persons */}
      {np && (
        <AuditPill
          label="Named persons"
          testId="preview-pill-named-persons"
          info={
            <>
              <p>
                Hallucination guardrail — Kalib AI extracts every proper-noun
                phrase from action titles and bodies, then cross-references
                them against the underlying signal corpus to confirm each
                person actually exists in the source material.
              </p>
              <p>
                <strong>Verified N</strong> — names that DID appear in the
                signal corpus → real people the LLM correctly named.
              </p>
              <p>
                <strong>Unverified N</strong> — names absent from the
                signal corpus → likely AI hallucinations. When this is &gt; 0
                the operator should review the flagged actions before the
                reveal goes live.
              </p>
            </>
          }
        >
          <span
            className={`px-1.5 py-0.5 border uppercase tracking-widest font-mono ${
              (np.unverified_count || 0) > 0
                ? "bg-red-50 text-red-800 border-red-300"
                : "bg-emerald-50 text-emerald-800 border-emerald-300"
            }`}
            title={
              (np.unverified_count || 0) > 0
                ? `${np.unverified_count} candidate name(s) NOT found in the U-tier signal corpus — likely hallucinations.`
                : `${np.verified_count || 0} named persons verified against the signal corpus.`
            }
          >
            unverified {np.unverified_count || 0}
          </span>
          <span
            className="px-1.5 py-0.5 border bg-white text-zinc-700 border-zinc-300 uppercase tracking-widest font-mono"
            title="Total proper-noun candidates that DID appear in the U-tier signal corpus."
          >
            verified {np.verified_count || 0}
          </span>
        </AuditPill>
      )}

      {/* Pill 3 — citation links. Chunk 3.6 — `archive` branch added. */}
      {ca && (
        <AuditPill
          label="Citation links"
          testId="preview-pill-citations"
          info={
            <>
              <p>
                Every cited URL is HEAD-checked post-harvest. Dead links are
                downgraded so the user never clicks into a 404. When archive.org
                has a snapshot of a dead URL, Kalib AI links to the snapshot
                instead.
              </p>
              <p>
                <strong>Verified</strong> — HEAD/GET succeeded; clickable.
              </p>
              <p>
                <strong>Archive</strong> — original URL was dead, but a
                Wayback Machine snapshot exists. Renderer links to the
                archive with an <em>(archived)</em> suffix.
              </p>
              <p>
                <strong>Trusted</strong> — original URL failed HEAD but
                the domain is on the trust-bypass list (FT, Bloomberg,
                etc. that 4xx HEAD due to paywalls). Treated as real but
                not clickable.
              </p>
              <p>
                <strong>Dead</strong> — failed HEAD, no archive snapshot,
                not on the trust list. Renderer shows "outlet · date"
                plain text — no broken link surfaced to the user.
              </p>
            </>
          }
        >
          <span
            className="px-1.5 py-0.5 border bg-emerald-50 text-emerald-800 border-emerald-300 uppercase tracking-widest font-mono"
            title="Sources where url_verified=true (HEAD/GET succeeded post-harvest)."
          >
            verified {ca.verified_count || 0}
          </span>
          <span
            className={`px-1.5 py-0.5 border uppercase tracking-widest font-mono ${
              (ca.archive_count || 0) > 0
                ? "bg-sky-50 text-sky-800 border-sky-300"
                : "bg-zinc-100 text-zinc-700 border-zinc-300"
            }`}
            title="Chunk 3.6 — sources where the original URL failed HEAD but archive.org has a snapshot. Renderer links to the archive with '(archived)' suffix."
          >
            archive {ca.archive_count || 0}
          </span>
          <span
            className="px-1.5 py-0.5 border bg-amber-50 text-amber-800 border-amber-300 uppercase tracking-widest font-mono"
            title="Sources without HEAD-verification but on the trusted-domain bypass list."
          >
            trusted {ca.trusted_only_count || 0}
          </span>
          <span
            className={`px-1.5 py-0.5 border uppercase tracking-widest font-mono ${
              (ca.dead_count || 0) > 0
                ? "bg-red-50 text-red-800 border-red-300"
                : "bg-zinc-100 text-zinc-700 border-zinc-300"
            }`}
            title="Sources with no HEAD verification AND not on the trusted-domain list AND no archive.org snapshot."
          >
            dead {ca.dead_count || 0}
          </span>
        </AuditPill>
      )}

      {/* Pill 4 — tier differentiation */}
      {td !== null && (
        <AuditPill
          label="Tier differentiation"
          testId="preview-pill-tier-diff"
          info={
            <>
              <p>
                Pairwise Jaccard token similarity across IC / Manager /
                Leader action titles, expressed as a 0.00–1.00 score:
              </p>
              <p>
                <strong>1.00</strong> — IC, Manager, and Leader cards are
                using totally distinct vocabulary. Each tier is genuinely
                speaking at its own altitude.
              </p>
              <p>
                <strong>≥ 0.60</strong> — healthy differentiation
                (emerald). This is the target.
              </p>
              <p>
                <strong>0.40 – 0.60</strong> — borderline (amber). Some
                tier collapse; review.
              </p>
              <p>
                <strong>&lt; 0.40</strong> — unhealthy (red). The three
                tiers are essentially saying the same thing at different
                volumes. Synthesis prompt regression suspected.
              </p>
            </>
          }
        >
          <span
            className={`px-1.5 py-0.5 border uppercase tracking-widest font-mono ${
              td >= 0.6
                ? "bg-emerald-50 text-emerald-800 border-emerald-300"
                : td >= 0.4
                ? "bg-amber-50 text-amber-800 border-amber-300"
                : "bg-red-50 text-red-800 border-red-300"
            }`}
            title="1.0 = IC/Manager/Leader titles totally distinct. <0.4 = the three tiers are saying the same thing."
          >
            score {td.toFixed(2)}
          </span>
        </AuditPill>
      )}
    </div>
  );
}

function AuditPill({ label, children, testId, title, info }) {
  return (
    <div
      className="border border-zinc-200 bg-zinc-50 px-2.5 py-1.5 text-[11px] flex items-center justify-between gap-2 flex-wrap"
      data-testid={testId}
      title={title}
    >
      <span className="flex items-center gap-1 font-medium uppercase tracking-widest text-zinc-900">
        {label}
        {/* Small ? button per pill — opens a popover with the plain-English
            definition + an explanation of what each chip means. Click to
            open / click outside to dismiss; doesn't disturb the pill row. */}
        {info && (
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="ml-0.5 inline-flex items-center justify-center w-3.5 h-3.5 text-zinc-500 hover:text-zinc-900 transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-amber-700"
                data-testid={`${testId}-info-trigger`}
                aria-label={`What does ${label} mean?`}
              >
                <HelpCircle className="w-3.5 h-3.5" strokeWidth={1.75} />
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className="w-[300px] rounded-none border-zinc-300 bg-white p-3 text-[12px] leading-relaxed text-zinc-900 shadow-lg"
              data-testid={`${testId}-info-popover`}
            >
              <p className="font-medium uppercase tracking-widest text-[11px] text-zinc-700 mb-2">
                {label}
              </p>
              <div className="space-y-1.5 normal-case tracking-normal">
                {info}
              </div>
            </PopoverContent>
          </Popover>
        )}
      </span>
      <span className="flex items-center gap-2 flex-wrap">
        {children}
      </span>
    </div>
  );
}


// Chunk 3.11 (2026-05-05) — Action-set QA debug panel.
// Renders the deterministic QA block (`action_set_qa`) the backend
// stamps on every calibrated reveal. Collapsible to keep the page
// uncluttered; opens by default when QA failed so problems are visible
// at a glance.
function ActionSetQaPanel({ qa }) {
  const [open, setOpen] = useState(true);
  if (!qa || qa.qa_skipped_reason === "no_actions") return null;

  const overlapPairs = qa.overlap_pairs || [];
  const toneViolations = qa.tone_violations || [];
  const citationWarnings = qa.citation_label_warnings || [];
  const droppedCards = qa.dropped_cards || [];
  const multiAdjacency = qa.multi_adjacency || [];
  const mes = typeof qa.mutual_exclusivity_score === "number" ? qa.mutual_exclusivity_score : null;
  const passed = qa.qa_passed !== false;

  // Auto-collapse when everything is clean to reduce noise.
  // (Operator can still click to expand.)
  const everythingClean = passed
    && overlapPairs.length === 0
    && toneViolations.length === 0
    && citationWarnings.length === 0
    && droppedCards.length === 0
    && multiAdjacency.length === 0
    && (qa.citation_chips_suppressed || 0) === 0;

  return (
    <div
      className={`mb-6 border ${passed ? "border-zinc-300" : "border-red-300"} bg-white`}
      data-testid="action-set-qa-panel"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-3 py-2 text-[12px] uppercase tracking-widest font-mono text-zinc-700 hover:bg-zinc-50"
        data-testid="action-set-qa-toggle"
      >
        <span className="flex items-center gap-2">
          <span className="font-medium">Action-set QA</span>
          <span
            className={`px-1.5 py-0.5 border ${
              passed
                ? "bg-emerald-50 text-emerald-800 border-emerald-300"
                : "bg-red-50 text-red-800 border-red-300"
            }`}
            data-testid="qa-passed-badge"
          >
            {passed ? "passed" : "failed"}
          </span>
          {mes !== null && (
            <span
              className="px-1.5 py-0.5 border bg-white text-zinc-700 border-zinc-300"
              title="1 - max pairwise Jaccard similarity across the final card set. Higher = more mutually exclusive."
              data-testid="qa-mes-badge"
            >
              MES {mes.toFixed(2)}
            </span>
          )}
          {(qa.citation_chips_suppressed || 0) > 0 && (
            <span
              className="px-1.5 py-0.5 border bg-amber-50 text-amber-800 border-amber-300"
              title="Citation chips automatically suppressed because the visible source label looked implausible for the claim."
              data-testid="qa-suppressed-badge"
            >
              {qa.citation_chips_suppressed} chip(s) suppressed
            </span>
          )}
          {droppedCards.length > 0 && (
            <span
              className="px-1.5 py-0.5 border bg-amber-50 text-amber-800 border-amber-300"
              title="Cards dropped during calibration — by jtbd_rewrite (LLM failure / consultant-speak detector / no alternative JTBD) or by the post-rewrite fail-drop pass."
              data-testid="qa-dropped-badge"
            >
              {droppedCards.length} card{droppedCards.length === 1 ? "" : "s"} dropped
            </span>
          )}
          {(qa.multi_adjacency || []).length > 0 && (
            <span
              className="px-1.5 py-0.5 border bg-amber-50 text-amber-800 border-amber-300"
              title="Bridge cards — single cards overlapping with 2+ other cards above the WARN threshold. Diagnostic only; doesn't block qa_passed."
              data-testid="qa-multi-adjacency-badge"
            >
              {qa.multi_adjacency.length} bridge card{qa.multi_adjacency.length === 1 ? "" : "s"}
            </span>
          )}
        </span>
        <span className="text-zinc-400">{everythingClean ? (open ? "−" : "+ details") : (open ? "− hide" : "+ details")}</span>
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 text-[12px] text-zinc-800 space-y-3" data-testid="action-set-qa-body">
          {/* Overlap pairs */}
          <div>
            <p className="uppercase tracking-widest text-[10px] text-zinc-500 mb-1">
              Mutual exclusivity ({overlapPairs.length} overlap pair{overlapPairs.length === 1 ? "" : "s"})
            </p>
            {overlapPairs.length === 0 ? (
              <p className="text-zinc-500">No card pair exceeded the overlap threshold.</p>
            ) : (
              <ul className="space-y-1.5" data-testid="qa-overlap-pairs">
                {overlapPairs.map((p, idx) => (
                  <li key={idx} className="border-l-2 border-amber-400 pl-2">
                    <p className="font-mono text-[11px] text-zinc-500">
                      cards {p.i} ↔ {p.j} · sim {p.similarity?.toFixed?.(2)}
                    </p>
                    <p className="text-zinc-800">{p.reason}</p>
                    <p className="text-zinc-500 text-[11px]">
                      "{p.title_i}" vs "{p.title_j}"
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Tone violations */}
          <div>
            <p className="uppercase tracking-widest text-[10px] text-zinc-500 mb-1">
              Tone ({toneViolations.length} violation{toneViolations.length === 1 ? "" : "s"})
            </p>
            {toneViolations.length === 0 ? (
              <p className="text-zinc-500">No command-heavy lead phrases detected.</p>
            ) : (
              <ul className="space-y-1" data-testid="qa-tone-violations">
                {toneViolations.map((v, idx) => (
                  <li key={idx} className="border-l-2 border-amber-400 pl-2">
                    <p className="font-mono text-[11px] text-zinc-500">card {v.action_idx} · {v.phrase}</p>
                    <p className="text-zinc-800">{v.line}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Citation warnings */}
          <div>
            <p className="uppercase tracking-widest text-[10px] text-zinc-500 mb-1">
              Citation labels ({citationWarnings.length} warning{citationWarnings.length === 1 ? "" : "s"})
            </p>
            {citationWarnings.length === 0 ? (
              <p className="text-zinc-500">No citation-label mismatches detected.</p>
            ) : (
              <ul className="space-y-1" data-testid="qa-citation-warnings">
                {citationWarnings.map((w, idx) => (
                  <li key={idx} className="border-l-2 border-red-400 pl-2">
                    <p className="font-mono text-[11px] text-zinc-500">
                      card {w.action_idx} · source {w.source_idx} · suppressed
                    </p>
                    <p className="text-zinc-800">
                      <span className="font-medium">"{w.source_label}"</span> — {w.reason}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* v6.5.1 — Multi-adjacency bridge cards */}
          <div data-testid="qa-multi-adjacency-section">
            <p className="uppercase tracking-widest text-[10px] text-zinc-500 mb-1">
              Bridge cards ({multiAdjacency.length})
            </p>
            {multiAdjacency.length === 0 ? (
              <p className="text-zinc-500">No card overlaps with 2+ others above the WARN threshold.</p>
            ) : (
              <ul className="space-y-1.5" data-testid="qa-multi-adjacency-list">
                {multiAdjacency.map((m, idx) => (
                  <li key={idx} className="border-l-2 border-amber-400 pl-2" data-testid="qa-bridge-card-item">
                    <p className="font-mono text-[11px] text-zinc-500">
                      card idx {m.card_index} · {m.card_job_type || "?"} ·{" "}
                      <span className="text-amber-700 font-medium">
                        overlaps with {m.neighbour_count} other cards
                      </span>
                    </p>
                    <p className="text-zinc-800">
                      <span className="font-medium">"{m.card_title || "(no title)"}"</span>
                    </p>
                    <ul className="text-[11px] text-zinc-600 mt-0.5 space-y-0.5">
                      {(m.neighbour_pairs || []).map((p, pidx) => {
                        const otherIdx = p.i === m.card_index ? p.j : p.i;
                        const otherTitle = p.i === m.card_index ? p.title_j : p.title_i;
                        return (
                          <li key={pidx}>
                            ↳ vs card {otherIdx} ({p.severity}, sim={p.similarity}):{" "}
                            <span className="text-zinc-700">"{otherTitle}"</span>
                          </li>
                        );
                      })}
                    </ul>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* v6.5.1 — Dropped cards (rewrite-time + post-rewrite fail-drop) */}
          <DroppedCardsSection droppedCards={droppedCards} />
        </div>
      )}
    </div>
  );
}

// Instruction 3 (2026-05-18) — Automated Checks panel.
// Runs the 6 deterministic checks from /api/eval/automated-checks against the
// currently-previewed reveal. Compact, on-demand, zero-LLM. Mirrors the
// look-and-feel of ActionSetQaPanel for visual consistency.
function AutomatedChecksPanel({ accountName, generationCycle }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const canRun = !!(accountName && generationCycle);

  const runChecks = useCallback(async () => {
    if (!canRun) return;
    setRunning(true);
    setError("");
    try {
      const { data } = await API.get("/eval/automated-checks", {
        params: { account: accountName, cycle: generationCycle },
      });
      setResult(data);
    } catch (e) {
      setError(formatApiError(e) || "Failed to run checks");
    } finally {
      setRunning(false);
    }
  }, [accountName, generationCycle, canRun]);

  const overallPass = result?.overall_pass === true;
  const totalFailures = result?.total_failures || 0;

  return (
    <div
      className={`mb-6 border ${result ? (overallPass ? "border-emerald-300" : "border-red-300") : "border-zinc-300"} bg-white`}
      data-testid="automated-checks-panel"
    >
      <div className="w-full flex items-center justify-between px-3 py-2 text-[12px] uppercase tracking-widest font-mono text-zinc-700">
        <span className="flex items-center gap-2">
          <span className="font-medium">Automated checks</span>
          {result && (
            <span
              className={`px-1.5 py-0.5 border ${
                overallPass
                  ? "bg-emerald-50 text-emerald-800 border-emerald-300"
                  : "bg-red-50 text-red-800 border-red-300"
              }`}
              data-testid="automated-checks-overall-badge"
            >
              {overallPass ? "passed" : `${totalFailures} failure${totalFailures === 1 ? "" : "s"}`}
            </span>
          )}
          {result && (
            <span className="text-zinc-500 normal-case tracking-normal">
              {result.total_cards} card{result.total_cards === 1 ? "" : "s"}
            </span>
          )}
        </span>
        <button
          type="button"
          onClick={runChecks}
          disabled={running || !canRun}
          className="text-[11px] uppercase tracking-widest border border-zinc-300 px-2 py-1 hover:bg-zinc-50 disabled:opacity-50"
          data-testid="automated-checks-run-btn"
          title={canRun ? "Run the 6 deterministic checks against this reveal" : "Preview a reveal first"}
        >
          {running ? "Running…" : result ? "Re-run" : "Run checks"}
        </button>
      </div>
      {error && (
        <div className="px-3 pb-2 text-[12px] text-red-700" data-testid="automated-checks-error">
          {error}
        </div>
      )}
      {result && (
        <div className="px-3 pb-3 pt-1" data-testid="automated-checks-body">
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(result.checks || {}).map(([name, r]) => {
              const pass = r.failed === 0;
              return (
                <span
                  key={name}
                  data-testid={`automated-check-${name}`}
                  className={`px-1.5 py-0.5 border text-[11px] font-mono ${
                    pass
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : "bg-red-50 text-red-700 border-red-200"
                  }`}
                  title={name.replace(/_/g, " ")}
                >
                  {pass ? "✓" : "✗"} {name.replace(/_/g, " ")}
                  {!pass && <span className="ml-1 font-semibold">{r.failed}</span>}
                </span>
              );
            })}
          </div>
          {!overallPass && result.summary && (
            <p className="mt-2 text-[12px] text-red-700 font-mono">{result.summary}</p>
          )}
        </div>
      )}
    </div>
  );
}


const STAGE_LABEL = {
  no_alternative_jtbd:           "no alternative JTBD",
  jtbd_rewrite_llm_exception:    "rewrite LLM exception",
  jtbd_rewrite_llm_malformed:    "rewrite LLM malformed",
  jtbd_rewrite_consultant_speak: "consultant-speak detected",
  post_rewrite_fail_drop:        "post-rewrite fail-drop",
};


// Instruction 5 Phase B (2026-05-19) — Citation Check panel.
// Calls /api/eval/citation-check/{reveal_id} on demand. Surfaces, per cited
// source, the resolved hyperlink status (live / wayback / unverified /
// unavailable) and which cards cite it. Read-only audit; mirrors
// AutomatedChecksPanel's look-and-feel.
function CitationCheckPanel({ revealId }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const canRun = !!revealId;

  const runCheck = useCallback(async () => {
    if (!canRun) return;
    setRunning(true);
    setError("");
    try {
      const { data } = await API.get(`/eval/citation-check/${revealId}`);
      setResult(data);
    } catch (e) {
      setError(formatApiError(e) || "Failed to run citation check");
    } finally {
      setRunning(false);
    }
  }, [revealId, canRun]);

  const summary = result?.summary || {};
  const resolvable = (summary.live || 0) + (summary.wayback || 0);
  const allResolvable = result && result.total_sources > 0 && resolvable === result.total_sources;

  return (
    <div
      className={`mb-6 border ${result ? (allResolvable ? "border-emerald-300" : "border-amber-300") : "border-zinc-300"} bg-white`}
      data-testid="citation-check-panel"
    >
      <div className="w-full flex items-center justify-between px-3 py-2 text-[12px] uppercase tracking-widest font-mono text-zinc-700">
        <span className="flex items-center gap-2">
          <span className="font-medium">Citation check</span>
          {result && (
            <span
              className={`px-1.5 py-0.5 border ${
                allResolvable
                  ? "bg-emerald-50 text-emerald-800 border-emerald-300"
                  : "bg-amber-50 text-amber-800 border-amber-300"
              }`}
              data-testid="citation-check-summary-badge"
            >
              {summary.resolvable_pct ?? 0}% resolvable
            </span>
          )}
          {result && (
            <span className="text-zinc-500 normal-case tracking-normal">
              {result.total_sources} source{result.total_sources === 1 ? "" : "s"}
              {summary.live ? ` · live ${summary.live}` : ""}
              {summary.wayback ? ` · wayback ${summary.wayback}` : ""}
              {summary.unverified ? ` · unverified ${summary.unverified}` : ""}
              {summary.unavailable ? ` · unavailable ${summary.unavailable}` : ""}
            </span>
          )}
        </span>
        <button
          type="button"
          onClick={runCheck}
          disabled={running || !canRun}
          className="text-[11px] uppercase tracking-widest border border-zinc-300 px-2 py-1 hover:bg-zinc-50 disabled:opacity-50"
          data-testid="citation-check-run-btn"
          title={canRun ? "Audit every cited source (live / wayback / unavailable)" : "Preview a reveal first"}
        >
          {running ? "Running…" : result ? "Re-run" : "Run check"}
        </button>
      </div>
      {error && (
        <div className="px-3 pb-2 text-[12px] text-red-700" data-testid="citation-check-error">
          {error}
        </div>
      )}
      {result && (
        <div className="px-3 pb-3 pt-1" data-testid="citation-check-body">
          <div className="space-y-1.5">
            {(result.sources || []).slice(0, 12).map((row) => {
              const statusColor = {
                live: "bg-emerald-50 text-emerald-700 border-emerald-200",
                wayback: "bg-amber-50 text-amber-800 border-amber-300",
                unverified: "bg-zinc-50 text-zinc-700 border-zinc-300",
                unavailable: "bg-red-50 text-red-700 border-red-200",
              }[row.status] || "bg-zinc-50 text-zinc-700 border-zinc-300";
              return (
                <div
                  key={row.source_index}
                  className="flex items-start gap-2 text-[12px] font-mono text-zinc-800"
                  data-testid={`citation-row-${row.source_index}`}
                >
                  <span className="text-zinc-500 mt-0.5 shrink-0">[{row.source_index}]</span>
                  <span className={`px-1.5 py-0.5 border ${statusColor} shrink-0 uppercase tracking-wider text-[10px]`}>
                    {row.status}
                  </span>
                  <span className="text-zinc-700 normal-case truncate" title={row.headline}>
                    {row.href ? (
                      <a
                        href={row.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-amber-800 hover:text-amber-900 underline decoration-amber-300"
                      >
                        {row.headline || row.source_name}
                      </a>
                    ) : (
                      <span>{row.headline || row.source_name}</span>
                    )}
                    {row.cited_by_count > 0 && (
                      <span className="ml-1.5 text-zinc-500">· {row.cited_by_count} card{row.cited_by_count === 1 ? "" : "s"}</span>
                    )}
                  </span>
                </div>
              );
            })}
            {result.sources && result.sources.length > 12 && (
              <div className="text-[11px] text-zinc-500 font-mono pt-1">
                + {result.sources.length - 12} more …
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


function DroppedCardsSection({ droppedCards }) {
  // Default collapsed — drops are usually a small number and operators
  // only need detail when they're hunting a specific card.
  const [open, setOpen] = useState(false);
  const count = droppedCards.length;
  return (
    <div data-testid="qa-dropped-cards-section">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between text-left mb-1 hover:bg-zinc-50 -mx-1 px-1 py-0.5"
        data-testid="qa-dropped-cards-toggle"
      >
        <span className="uppercase tracking-widest text-[10px] text-zinc-500">
          Dropped cards ({count})
        </span>
        {count > 0 && (
          <span className="text-[10px] text-zinc-400">{open ? "− hide" : "+ details"}</span>
        )}
      </button>
      {count === 0 ? (
        <p className="text-zinc-500">No cards dropped during calibration.</p>
      ) : !open ? (
        <p className="text-zinc-500 text-[11px]">
          {count} card{count === 1 ? "" : "s"} dropped — click to inspect each drop's
          original title, JTBD, reason, and pipeline stage.
        </p>
      ) : (
        <ul className="space-y-1.5" data-testid="qa-dropped-cards-list">
          {droppedCards.map((d, idx) => {
            const stageLabel = STAGE_LABEL[d.dropped_at_stage] || d.dropped_at_stage || "unknown stage";
            const isConsultantSpeak = d.dropped_at_stage === "jtbd_rewrite_consultant_speak";
            return (
              <li
                key={idx}
                className={`border-l-2 pl-2 ${isConsultantSpeak ? "border-red-400" : "border-amber-400"}`}
                data-testid="qa-dropped-card-item"
              >
                <p className="font-mono text-[11px] text-zinc-500">
                  card idx {d.action_index ?? "?"} · {d.original_jtbd || "?"}
                  {d.target_jtbd ? ` → ${d.target_jtbd}` : ""}
                  {" · "}
                  <span className={isConsultantSpeak ? "text-red-700 font-medium" : "text-amber-700"}>
                    {stageLabel}
                  </span>
                </p>
                <p className="text-zinc-800">
                  <span className="font-medium">"{d.original_title || "(no title)"}"</span>
                </p>
                <p className="text-zinc-600 text-[11px]">{d.drop_reason || "(no reason)"}</p>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
